import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './hooks/useAuth'
import ConsentBanner from './components/ConsentBanner'
import { initUTMTracker } from './lib/utmTracker'

// ✅ Lazy loading — cada página carga solo cuando se necesita
const Landing = lazy(() => import('./pages/auth/Landing'))
const LoginPage = lazy(() => import('./pages/auth/LoginPage'))
const Terminos = lazy(() => import('./pages/Terminos'))
const Privacidad = lazy(() => import('./pages/Privacidad'))
const NotFound = lazy(() => import('./pages/NotFound'))

// Admin
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'))
const AdminInventario = lazy(() => import('./pages/admin/AdminInventario'))
const AdminClientes = lazy(() => import('./pages/admin/AdminClientes'))
const AdminProductos = lazy(() => import('./pages/admin/AdminProductos'))
const AdminSedes = lazy(() => import('./pages/admin/AdminSedes'))
const AdminNotificaciones = lazy(() => import('./pages/admin/AdminNotificaciones'))
const AdminMayoristas = lazy(() => import('./pages/admin/AdminMayoristas'))
const AdminColaboradores = lazy(() => import('./pages/admin/AdminColaboradores'))
const AdminAquaLearn = lazy(() => import('./pages/admin/AdminAquaLearn'))
const AdminCompras = lazy(() => import('./pages/admin/AdminCompras'))
const AdminEbooks = lazy(() => import('./pages/admin/AdminEbooks'))
const AdminFinanzas = lazy(() => import('./pages/admin/AdminFinanzas'))
const AdminBodega = lazy(() => import('./pages/admin/AdminBodega'))
const AdminPromociones  = lazy(() => import('./pages/admin/AdminPromociones'))
const AdminHighlights   = lazy(() => import('./pages/admin/AdminHighlights'))
const AdminAuditoria   = lazy(() => import('./pages/admin/AdminAuditoria'))
const AdminReportes    = lazy(() => import('./pages/admin/AdminReportes'))
const AdminPulpin      = lazy(() => import('./pages/admin/AdminPulpin'))
const AdminPulpinBridge = lazy(() => import('./pages/admin/AdminPulpinBridge'))
const AdminPulpinContactos = lazy(() => import('./pages/admin/AdminPulpinContactos'))
const AdminCostos       = lazy(() => import('./pages/admin/AdminCostos'))
const AdminPuntos       = lazy(() => import('./pages/admin/AdminPuntos'))
const AdminProduccion   = lazy(() => import('./pages/admin/AdminProduccion'))
const AdminMargenes     = lazy(() => import('./pages/admin/AdminMargenes'))
const AdminDespachos    = lazy(() => import('./pages/admin/AdminDespachos'))
const AdminCotizaciones = lazy(() => import('./pages/admin/AdminCotizaciones'))
const AdminSupervisor   = lazy(() => import('./pages/admin/AdminSupervisor'))
const AdminAsistencia   = lazy(() => import('./pages/admin/AdminAsistencia'))
const AdminAccionistas  = lazy(() => import('./pages/admin/AdminAccionistas'))
const AdminLlegadas     = lazy(() => import('./pages/admin/AdminLlegadas'))
const AdminVida         = lazy(() => import('./pages/admin/AdminVida'))
const AdminColabTareas  = lazy(() => import('./pages/admin/AdminColabTareas'))
const AdminRifa         = lazy(() => import('./pages/admin/AdminRifa'))

// Accionista
const AccionistaDashboard  = lazy(() => import('./pages/accionista/AccionistaDashboard'))
const AccionistaAcciones   = lazy(() => import('./pages/accionista/AccionistaAcciones'))
const AccionistaInformes   = lazy(() => import('./pages/accionista/AccionistaInformes'))
const AccionistaDividendos = lazy(() => import('./pages/accionista/AccionistaDividendos'))
const AccionistaDocumentos = lazy(() => import('./pages/accionista/AccionistaDocumentos'))
const AccionistaPerfil     = lazy(() => import('./pages/accionista/AccionistaPerfil'))
const AccionistaPrestamos  = lazy(() => import('./pages/accionista/AccionistaPrestamos'))

// Supervisor
const SupervisorDashboard       = lazy(() => import('./pages/supervisor/SupervisorDashboard'))
const SupervisorTareas          = lazy(() => import('./pages/supervisor/SupervisorTareas'))
const SupervisorContabilidad    = lazy(() => import('./pages/supervisor/SupervisorContabilidad'))
const SupervisorColaboradores   = lazy(() => import('./pages/supervisor/SupervisorColaboradores'))
const SupervisorInsumos         = lazy(() => import('./pages/supervisor/SupervisorInsumos'))
const SupervisorActivos         = lazy(() => import('./pages/supervisor/SupervisorActivos'))
const SupervisorInventarioToro  = lazy(() => import('./pages/supervisor/SupervisorInventarioToro'))
const SupervisorCapacitacion    = lazy(() => import('./pages/supervisor/SupervisorCapacitacion'))
const AdminInsumos              = lazy(() => import('./pages/admin/AdminInsumos'))
const AdminActivos              = lazy(() => import('./pages/admin/AdminActivos'))
const ColabInsumos              = lazy(() => import('./pages/colaborador/ColabInsumos'))
const ColabActivos              = lazy(() => import('./pages/colaborador/ColabActivos'))

// Producción
const ProduccionHome = lazy(() => import('./pages/produccion/ProduccionHome'))

// Colaborador
const ColabVentas = lazy(() => import('./pages/colaborador/ColabVentas'))
const ColabCaja = lazy(() => import('./pages/colaborador/ColabCaja'))
const ColabInventario = lazy(() => import('./pages/colaborador/ColabInventario'))
const ColabClientes = lazy(() => import('./pages/colaborador/ColabClientes'))
const ColabSede = lazy(() => import('./pages/colaborador/ColabSede'))
const ColabBienestar = lazy(() => import('./pages/colaborador/ColabBienestar'))
const ColabPedidos = lazy(() => import('./pages/colaborador/ColabPedidos'))
const ColabRecepcion = lazy(() => import('./pages/colaborador/ColabRecepcion'))
const ColabDespachos = lazy(() => import('./pages/colaborador/ColabDespachos'))
const ColabPerfil      = lazy(() => import('./pages/colaborador/ColabPerfil'))
const ColabAsistencia  = lazy(() => import('./pages/colaborador/ColabAsistencia'))
const ColabMayorista   = lazy(() => import('./pages/colaborador/ColabMayorista'))
const ColabEco         = lazy(() => import('./pages/colaborador/ColabEco'))
const ColabTareas         = lazy(() => import('./pages/colaborador/ColabTareas'))
const ColabCapacitacion   = lazy(() => import('./pages/colaborador/ColabCapacitacion'))
const ColabRifa           = lazy(() => import('./pages/colaborador/ColabRifa'))
const AdminCapacitacion   = lazy(() => import('./pages/admin/AdminCapacitacion'))


// Cliente
const ClienteHome = lazy(() => import('./pages/cliente/ClienteHome'))
const ClientePerfil = lazy(() => import('./pages/cliente/ClientePerfil'))
const ClientePuntos = lazy(() => import('./pages/cliente/ClientePuntos'))
const ClienteMascotas = lazy(() => import('./pages/cliente/ClienteMascotas'))
const ClienteCatalogo = lazy(() => import('./pages/cliente/ClienteCatalogo'))
const ClienteCheckout = lazy(() => import('./pages/cliente/ClienteCheckout'))
const ClienteDiario = lazy(() => import('./pages/cliente/ClienteDiario'))
const ClientePulpin = lazy(() => import('./pages/cliente/ClientePulpin'))
const ClientePerfilPublico = lazy(() => import('./pages/cliente/ClientePerfilPublico'))
const ClienteChat = lazy(() => import('./pages/cliente/ClienteChat'))
const ClienteConversaciones = lazy(() => import('./pages/cliente/ClienteConversaciones'))
const ClienteMisPedidos = lazy(() => import('./pages/cliente/ClienteMisPedidos'))
const ClienteJuego      = lazy(() => import('./pages/cliente/ClienteJuego'))
const ClienteEco        = lazy(() => import('./pages/cliente/ClienteEco'))
const ClienteBiblioteca = lazy(() => import('./pages/cliente/ClienteBiblioteca'))

// Mayorista
const MayoristaLayout = lazy(() => import('./pages/mayorista/MayoristaLayout'))
const MayoristaHome = lazy(() => import('./pages/mayorista/MayoristaHome'))
const MayoristaCatalogo = lazy(() => import('./pages/mayorista/MayoristaCatalogo'))
const MayoristaCheckout = lazy(() => import('./pages/mayorista/MayoristaCheckout'))
const MayoristaPedidos = lazy(() => import('./pages/mayorista/MayoristaPedidos'))
const MayoristaPerfil = lazy(() => import('./pages/mayorista/MayoristaPerfil'))
// Plugin pages
const MayoristaInventario = lazy(() => import('./pages/mayorista/MayoristaInventario'))
const MayoristaCaja = lazy(() => import('./pages/mayorista/MayoristaCaja'))
const MayoristaVentas = lazy(() => import('./pages/mayorista/MayoristaVentas'))
const MayoristaResumen = lazy(() => import('./pages/mayorista/MayoristaResumen'))

// Layouts — estos sí cargan siempre, son pequeños
import AccionistaLayout from './components/layout/AccionistaLayout'
import AdminLayout from './components/layout/AdminLayout'
import SupervisorLayout from './components/layout/SupervisorLayout'
import ColabLayout from './components/layout/ColabLayout'
import CapacitadorLayout from './components/layout/CapacitadorLayout'
import ClienteLayout from './components/layout/ClienteLayout'
import ProduccionLayout from './components/layout/ProduccionLayout'

// ✅ Componente de carga mientras la página se descarga
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-4 border-aqua-200 border-t-aqua-500 rounded-full animate-spin" />
        <p className="text-sm text-gray-400 font-semibold">Cargando...</p>
      </div>
    </div>
  )
}

function PrivateRoute({ children, roles }: { children: JSX.Element; roles: string[] }) {
  const user = useAuthStore(s => s.user)
  const ready = useAuthStore(s => s.ready)
  if (!ready) return null
  if (!user) return <Navigate to="/" replace />
  if (!roles.includes(user.role)) return <Navigate to="/" replace />
  return children
}

export default function App() {
  const loadFromStorage = useAuthStore(s => s.loadFromStorage)
  const user = useAuthStore(s => s.user)
  const ready = useAuthStore(s => s.ready)

  useEffect(() => {
    loadFromStorage()
    initUTMTracker()
  }, [])

  if (!ready) return null

  if (user && window.location.pathname === '/') {
    const redirects: Record<string, string> = {
      ADMIN: '/admin',
      COLABORADOR: '/colab/ventas',
      CLIENTE: '/cliente',
      MAYORISTA: '/mayorista',
      PRODUCCION: '/produccion',
      SUPERVISOR: '/supervisor',
      ACCIONISTA: '/accionista',
      CAPACITADOR: '/capacitador/capacitacion',
    }
    return <Navigate to={redirects[user.role] || '/'} replace />
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/terminos" element={<Terminos />} />
        <Route path="/privacidad" element={<Privacidad />} />
        <Route path="/admin" element={<PrivateRoute roles={['ADMIN']}><AdminLayout /></PrivateRoute>}>
          <Route index element={<AdminDashboard />} />
          <Route path="inventario" element={<AdminInventario />} />
          <Route path="clientes" element={<AdminClientes />} />
          <Route path="productos" element={<AdminProductos />} />
          <Route path="sedes" element={<AdminSedes />} />
          <Route path="notificaciones" element={<AdminNotificaciones />} />
          <Route path="mayoristas" element={<AdminMayoristas />} />
          <Route path="colaboradores" element={<AdminColaboradores />} />
          <Route path="aqualearn" element={<AdminAquaLearn />} />
          <Route path="aqualern"  element={<Navigate to="/admin/aqualearn" replace />} />
          <Route path="compras" element={<AdminCompras />} />
          <Route path="libros" element={<AdminEbooks />} />
          <Route path="finanzas" element={<AdminFinanzas />} />
          <Route path="bodega" element={<AdminBodega />} />
          <Route path="promociones" element={<AdminPromociones />} />
          <Route path="rifa"        element={<AdminRifa />} />
          <Route path="highlights"  element={<AdminHighlights />} />
          <Route path="auditoria"   element={<AdminAuditoria />} />
          <Route path="reportes"    element={<AdminReportes />} />
          <Route path="pulpin"      element={<AdminPulpin />} />
          <Route path="pulpin-bridge" element={<AdminPulpinBridge />} />
          <Route path="pulpin-contactos" element={<AdminPulpinContactos />} />
          <Route path="costos"      element={<AdminCostos />} />
          <Route path="puntos"      element={<AdminPuntos />} />
          <Route path="produccion"  element={<AdminProduccion />} />
          <Route path="margenes"    element={<AdminMargenes />} />
          <Route path="despachos"   element={<AdminDespachos />} />
          <Route path="cotizaciones" element={<AdminCotizaciones />} />
          <Route path="supervisor"   element={<AdminSupervisor />} />
          <Route path="asistencia"   element={<AdminAsistencia />} />
          <Route path="accionistas"  element={<AdminAccionistas />} />
          <Route path="insumos"      element={<AdminInsumos />} />
          <Route path="activos"      element={<AdminActivos />} />
          <Route path="llegadas"     element={<AdminLlegadas />} />
          <Route path="vida"         element={<AdminVida />} />
          <Route path="colab-tareas"   element={<AdminColabTareas />} />
          <Route path="capacitacion"   element={<AdminCapacitacion />} />
        </Route>
        <Route path="/accionista" element={<PrivateRoute roles={['ACCIONISTA']}><AccionistaLayout /></PrivateRoute>}>
          <Route index element={<AccionistaDashboard />} />
          <Route path="acciones"   element={<AccionistaAcciones />} />
          <Route path="informes"   element={<AccionistaInformes />} />
          <Route path="dividendos" element={<AccionistaDividendos />} />
          <Route path="documentos" element={<AccionistaDocumentos />} />
          <Route path="prestamos"  element={<AccionistaPrestamos />} />
          <Route path="perfil"     element={<AccionistaPerfil />} />
        </Route>
        <Route path="/supervisor" element={<PrivateRoute roles={['SUPERVISOR']}><SupervisorLayout /></PrivateRoute>}>
          <Route index element={<SupervisorDashboard />} />
          <Route path="tareas" element={<SupervisorTareas />} />
          <Route path="contabilidad" element={<SupervisorContabilidad />} />
          <Route path="produccion" element={<AdminProduccion />} />
          <Route path="bodega" element={<AdminBodega />} />
          <Route path="despachos" element={<AdminDespachos />} />
          <Route path="colaboradores" element={<SupervisorColaboradores />} />
          <Route path="productos" element={<AdminProductos />} />
          <Route path="insumos" element={<SupervisorInsumos />} />
          <Route path="infraestructura" element={<SupervisorActivos />} />
          <Route path="inventario-toro" element={<SupervisorInventarioToro />} />
          <Route path="capacitacion" element={<SupervisorCapacitacion />} />
          <Route path="vida" element={<AdminVida />} />
        </Route>
        <Route path="/produccion" element={<PrivateRoute roles={['PRODUCCION']}><ProduccionLayout /></PrivateRoute>}>
          <Route index element={<ProduccionHome />} />
        </Route>
        <Route path="/capacitador" element={<PrivateRoute roles={['CAPACITADOR']}><CapacitadorLayout /></PrivateRoute>}>
          <Route index element={<Navigate to="capacitacion" replace />} />
          <Route path="capacitacion" element={<AdminCapacitacion />} />
          <Route path="aqualeern" element={<AdminAquaLearn />} />
        </Route>
        <Route path="/colab" element={<PrivateRoute roles={['COLABORADOR']}><ColabLayout /></PrivateRoute>}>
          <Route index element={<Navigate to="ventas" replace />} />
          <Route path="ventas" element={<ColabVentas />} />
          <Route path="pedidos" element={<ColabPedidos />} />
          <Route path="caja" element={<ColabCaja />} />
          <Route path="inventario" element={<ColabInventario />} />
          <Route path="clientes" element={<ColabClientes />} />
          <Route path="sede" element={<ColabSede />} />
          <Route path="bienestar" element={<Navigate to="/colab/sede" replace />} />
          <Route path="recepcion" element={<ColabRecepcion />} />
          <Route path="despachos"   element={<ColabDespachos />} />
          <Route path="perfil"      element={<ColabPerfil />} />
          <Route path="asistencia"  element={<ColabAsistencia />} />
          <Route path="insumos"     element={<ColabInsumos />} />
          <Route path="activos"     element={<ColabActivos />} />
          <Route path="mayorista"  element={<ColabMayorista />} />
          <Route path="eco"        element={<ColabEco />} />
          <Route path="tareas"       element={<ColabTareas />} />
          <Route path="capacitacion" element={<ColabCapacitacion />} />
          <Route path="rifa"         element={<ColabRifa />} />
        </Route>
        {/* Catálogo público — sin login, solo pide cuenta al checkout */}
        <Route path="/catalogo" element={<ClienteCatalogo />} />

        <Route path="/cliente" element={<PrivateRoute roles={['CLIENTE']}><ClienteLayout /></PrivateRoute>}>
          <Route index element={<ClienteHome />} />
          <Route path="perfil" element={<ClientePerfil />} />
          <Route path="puntos" element={<ClientePuntos />} />
          <Route path="mascotas" element={<ClienteMascotas />} />
          <Route path="catalogo" element={<ClienteCatalogo />} />
          <Route path="checkout" element={<ClienteCheckout />} />
          <Route path="diario" element={<ClienteDiario />} />
          <Route path="pulpin" element={<ClientePulpin />} />
          <Route path="perfil/:perfilId" element={<ClientePerfilPublico />} />
          <Route path="chat/:perfilId" element={<ClienteChat />} />
          <Route path="mensajes" element={<ClienteConversaciones />} />
          <Route path="pedidos" element={<ClienteMisPedidos />} />
          <Route path="juego"   element={<ClienteJuego />} />
          <Route path="eco"     element={<ClienteEco />} />
          <Route path="biblioteca" element={<ClienteBiblioteca />} />
        </Route>
        <Route path="/mayorista" element={<PrivateRoute roles={['MAYORISTA']}><MayoristaLayout /></PrivateRoute>}>
          <Route index element={<MayoristaHome />} />
          <Route path="catalogo" element={<MayoristaCatalogo />} />
          <Route path="checkout" element={<MayoristaCheckout />} />
          <Route path="pedidos" element={<MayoristaPedidos />} />
          <Route path="perfil" element={<MayoristaPerfil />} />
          {/* Plugin pages */}
          <Route path="inventario" element={<MayoristaInventario />} />
          <Route path="caja" element={<MayoristaCaja />} />
          <Route path="ventas" element={<MayoristaVentas />} />
          <Route path="resumen" element={<MayoristaResumen />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
      <ConsentBanner />
    </Suspense>
  )
}
